import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, MapPin, Phone, Mail, Upload, X, Plus, Trash2 } from "lucide-react";
import { StatusBadge } from "../../components/StatusBadge";
import { api, euro, fileUrl, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";

const REJECT_REASONS = ["Te ver weg", "Geen tijd beschikbaar", "Niet mijn specialisatie", "Andere reden"];

export default function PartnerOrder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState(REJECT_REASONS[0]);
  const [apptDate, setApptDate] = useState("");
  const [apptNote, setApptNote] = useState("");
  const [woOpen, setWoOpen] = useState(false);

  const load = () => api.get(`/bookings/${id}`).then((r) => setOrder(r.data)).catch(() => navigate("/partner"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const act = async (fn) => { setBusy(true); try { await fn(); await load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); } };

  if (!order) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="animate-spin text-slate-400" /></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => navigate("/partner")} className="text-slate-500 hover:text-slate-900 flex items-center gap-1 text-sm mb-4" data-testid="partner-back"><ArrowLeft size={16} /> Terug</button>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h1 className="font-heading text-2xl font-bold text-slate-900">Opdracht {order.booking_number}</h1>
            <StatusBadge status={order.status} />
          </div>
          <div className="mt-4 grid sm:grid-cols-2 gap-4 text-sm">
            <Info label="Probleem" value={`${order.service_name} · ${order.quantity}x ${order.item_type}`} />
            <Info label="Materiaal" value={order.material} />
            <Info label="Voorkeur" value={order.appointment_preference} />
            <Info label="Vergoeding" value={<span className="text-green-700 font-bold">{euro(order.partner_fee)}</span>} />
          </div>
          <div className="mt-4 rounded-lg bg-slate-50 p-4 text-sm space-y-1">
            <p className="font-semibold text-slate-900">{order.customer.first_name} {order.customer.last_name}</p>
            <p className="flex items-center gap-1 text-slate-600"><MapPin size={14} /> {order.location.street} {order.location.house_number}, {order.location.postal_code} {order.location.city}</p>
            <p className="flex items-center gap-1 text-slate-600"><Phone size={14} /> {order.customer.phone}</p>
            <p className="flex items-center gap-1 text-slate-600"><Mail size={14} /> {order.customer.email}</p>
          </div>

          {order.photos?.length > 0 && (
            <div className="mt-4">
              <Label className="mb-2 block">Foto's van de klant</Label>
              <div className="flex gap-2 flex-wrap">
                {order.photos.map((p) => <img key={p.id} src={fileUrl(p.path)} alt="" className="h-24 w-24 object-cover rounded-lg border" />)}
              </div>
            </div>
          )}

          {order.appointment && (
            <div className="mt-4 rounded-lg border border-cyan-200 bg-cyan-50 p-3 text-sm text-cyan-900">
              Afspraak: {new Date(order.appointment.date).toLocaleString("nl-BE")} — {order.appointment.note}
            </div>
          )}

          {/* ACTIONS */}
          <div className="mt-6 flex flex-wrap gap-3">
            {order.status === "partner_assigned" && !order.partner_accepted && (
              <>
                <Button className="cta-orange rounded-full" disabled={busy} onClick={() => act(() => api.post(`/partner/orders/${id}/accept`))} data-testid="partner-accept-btn">Opdracht accepteren</Button>
                <Button variant="outline" className="rounded-full" onClick={() => setRejectOpen(true)} data-testid="partner-reject-btn">Weigeren</Button>
              </>
            )}
            {order.status === "appointment_pending" && (
              <div className="w-full rounded-lg border border-slate-200 p-4">
                <Label className="font-semibold">Afspraak voorstellen / bevestigen</Label>
                <div className="mt-2 grid sm:grid-cols-2 gap-3">
                  <Input type="datetime-local" value={apptDate} onChange={(e) => setApptDate(e.target.value)} data-testid="partner-appt-date" />
                  <Input placeholder="Notitie (bv. namiddag)" value={apptNote} onChange={(e) => setApptNote(e.target.value)} data-testid="partner-appt-note" />
                </div>
                <Button className="cta-orange rounded-full mt-3" disabled={busy || !apptDate} onClick={() => act(() => api.post(`/partner/orders/${id}/appointment`, { date: new Date(apptDate).toISOString(), note: apptNote }))} data-testid="partner-confirm-appt-btn">Afspraak bevestigen</Button>
              </div>
            )}
            {order.status === "appointment_confirmed" && (
              <Button className="cta-orange rounded-full" disabled={busy} onClick={() => act(() => api.post(`/partner/orders/${id}/start`))} data-testid="partner-start-btn">Opdracht uitvoeren</Button>
            )}
            {order.status === "in_progress" && (
              <Button className="cta-orange rounded-full" onClick={() => setWoOpen(true)} data-testid="partner-workorder-btn">Werkbon invullen</Button>
            )}
            {order.status === "completed" && order.work_order && (
              <div className="w-full rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-900">
                <p className="font-semibold">Werkbon ingediend — opdracht afgerond</p>
                <p className="mt-1">{order.work_order.work_done}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <RejectDialog open={rejectOpen} setOpen={setRejectOpen} reason={rejectReason} setReason={setRejectReason}
        onConfirm={() => act(async () => { await api.post(`/partner/orders/${id}/reject`, { reason: rejectReason }); setRejectOpen(false); navigate("/partner"); })} busy={busy} />
      <WorkOrderDialog open={woOpen} setOpen={setWoOpen} orderId={id} onDone={() => { setWoOpen(false); load(); }} />
    </div>
  );
}

function Info({ label, value }) {
  return <div><p className="text-slate-500 text-xs">{label}</p><p className="font-medium text-slate-900">{value}</p></div>;
}

function RejectDialog({ open, setOpen, reason, setReason, onConfirm, busy }) {
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="reject-dialog">
        <DialogHeader><DialogTitle>Opdracht weigeren</DialogTitle></DialogHeader>
        <div className="space-y-2">
          {REJECT_REASONS.map((r) => (
            <button key={r} onClick={() => setReason(r)} data-testid={`reject-reason-${r}`} className={`w-full text-left p-3 rounded-lg border-2 ${reason === r ? "border-brand-orange bg-orange-50" : "border-slate-200"}`}>{r}</button>
          ))}
        </div>
        <Button variant="destructive" className="rounded-full mt-2" disabled={busy} onClick={onConfirm} data-testid="reject-confirm-btn">Bevestig weigering</Button>
      </DialogContent>
    </Dialog>
  );
}

function WorkOrderDialog({ open, setOpen, orderId, onDone }) {
  const [wo, setWo] = useState({ work_done: "", problem_solved: "ja", parts_used: [], extra_work: { has_extra: false, description: "", amount: 0, photos: [] }, after_photos: [], remark: "", customer_agreed: false });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const uploadPhotos = async (e, target) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ id: data.id, path: data.path, filename: data.filename });
      }
      if (target === "after") setWo((w) => ({ ...w, after_photos: [...w.after_photos, ...uploaded] }));
      else setWo((w) => ({ ...w, extra_work: { ...w.extra_work, photos: [...w.extra_work.photos, ...uploaded] } }));
    } finally { setUploading(false); }
  };

  const addPart = () => setWo((w) => ({ ...w, parts_used: [...w.parts_used, { name: "", quantity: 1, price: 0 }] }));
  const updatePart = (i, patch) => setWo((w) => ({ ...w, parts_used: w.parts_used.map((p, idx) => idx === i ? { ...p, ...patch } : p) }));
  const removePart = (i) => setWo((w) => ({ ...w, parts_used: w.parts_used.filter((_, idx) => idx !== i) }));

  const submit = async () => {
    if (!wo.work_done) return toast.error("Vul de uitgevoerde werken in");
    if (!wo.customer_agreed) return toast.error("Klant akkoord is vereist");
    setBusy(true);
    try {
      await api.post(`/partner/orders/${orderId}/workorder`, wo);
      toast.success("Werkbon ingediend, opdracht afgerond!");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="workorder-dialog">
        <DialogHeader><DialogTitle>Digitale werkbon</DialogTitle></DialogHeader>
        <div className="space-y-5">
          <div>
            <Label>Uitgevoerde werken</Label>
            <Textarea className="mt-1" value={wo.work_done} onChange={(e) => setWo({ ...wo, work_done: e.target.value })} data-testid="wo-work-done" />
          </div>
          <div>
            <Label>Probleem opgelost?</Label>
            <div className="mt-1 flex gap-2">
              {["ja", "gedeeltelijk", "nee"].map((v) => (
                <button key={v} onClick={() => setWo({ ...wo, problem_solved: v })} data-testid={`wo-solved-${v}`} className={`px-4 py-2 rounded-lg border-2 capitalize ${wo.problem_solved === v ? "border-brand-orange bg-orange-50" : "border-slate-200"}`}>{v}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label>Onderdelen gebruikt</Label>
              <Button variant="outline" size="sm" onClick={addPart} data-testid="wo-add-part"><Plus size={14} className="mr-1" /> Toevoegen</Button>
            </div>
            {wo.parts_used.map((p, i) => (
              <div key={i} className="mt-2 flex gap-2 items-center">
                <Input placeholder="Onderdeel" value={p.name} onChange={(e) => updatePart(i, { name: e.target.value })} data-testid={`wo-part-name-${i}`} />
                <Input type="number" className="w-20" placeholder="Aantal" value={p.quantity} onChange={(e) => updatePart(i, { quantity: Number(e.target.value) })} data-testid={`wo-part-qty-${i}`} />
                <Input type="number" className="w-24" placeholder="Prijs" value={p.price} onChange={(e) => updatePart(i, { price: Number(e.target.value) })} data-testid={`wo-part-price-${i}`} />
                <button onClick={() => removePart(i)}><Trash2 size={16} className="text-red-500" /></button>
              </div>
            ))}
          </div>
          <div>
            <label className="flex items-center gap-2"><Checkbox checked={wo.extra_work.has_extra} onCheckedChange={(v) => setWo({ ...wo, extra_work: { ...wo.extra_work, has_extra: !!v } })} data-testid="wo-extra-toggle" /> Extra werkzaamheden</label>
            {wo.extra_work.has_extra && (
              <div className="mt-2 space-y-2 pl-6">
                <Textarea placeholder="Beschrijving" value={wo.extra_work.description} onChange={(e) => setWo({ ...wo, extra_work: { ...wo.extra_work, description: e.target.value } })} data-testid="wo-extra-desc" />
                <Input type="number" placeholder="Bedrag" value={wo.extra_work.amount} onChange={(e) => setWo({ ...wo, extra_work: { ...wo.extra_work, amount: Number(e.target.value) } })} data-testid="wo-extra-amount" />
              </div>
            )}
          </div>
          <div>
            <Label>Foto's na uitvoering</Label>
            <label className="mt-1 flex items-center gap-2 rounded-lg border-2 border-dashed border-slate-300 p-4 cursor-pointer text-sm text-slate-500">
              {uploading ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />} Foto's uploaden
              <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadPhotos(e, "after")} data-testid="wo-photo-input" />
            </label>
            <div className="flex gap-2 mt-2 flex-wrap">
              {wo.after_photos.map((p) => <img key={p.id} src={fileUrl(p.path)} alt="" className="h-16 w-16 object-cover rounded border" />)}
            </div>
          </div>
          <div>
            <Label>Opmerking voor RamenHersteller.be</Label>
            <Textarea className="mt-1" value={wo.remark} onChange={(e) => setWo({ ...wo, remark: e.target.value })} data-testid="wo-remark" />
          </div>
          <label className="flex items-center gap-2 p-3 rounded-lg bg-slate-50 border border-slate-200">
            <Checkbox checked={wo.customer_agreed} onCheckedChange={(v) => setWo({ ...wo, customer_agreed: !!v })} data-testid="wo-customer-agreed" />
            <span className="text-sm font-medium">De klant is akkoord met de uitgevoerde werken</span>
          </label>
          <Button className="cta-orange rounded-full w-full py-6" disabled={busy} onClick={submit} data-testid="wo-submit-btn">
            {busy ? <Loader2 className="animate-spin" /> : "Opdracht afronden"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
